// 환경별 도메인 전환(2026-07-28): 신규 canonical 호스트만 판정한다.
//
// 기존 호스트는 null 을 반환해 각 호출부의 기존 분기가 그대로 동작한다(동작 변화 0).
// 신규 dev/stage 호스트가 열리기 전까지 이 모듈은 사실상 no-op 이다.
//
// 반드시 기존 livee.kr 판정보다 먼저 호출해야 한다.
// ".dev.livee.kr" 은 ".livee.kr" 로도 끝나므로, 순서가 뒤바뀌면 dev 주소가 prod 로 판정된다.
//
// prod 는 도메인을 바꾸지 않으므로(DM-03) 여기서 다루지 않는다.
// hub/console/api 에 같은 파일이 3벌 있고 계약 테스트가 드리프트를 막는다.
// docs/prd/PRD_환경별_도메인전환_구현_20260728.md 참조.
//
// CommonJS 로 둔다. console 의 playerUnmount.js 가 require 로 쓰고,
// 나머지 호출부는 import 로 쓴다(ESM → CJS 방향은 webpack/babel 이 안전하게 처리한다).

const CANONICAL_ENV_SUFFIXES = [
  { suffix: ".dev.livee.kr", env: "dev" },
  { suffix: ".stage.livee.kr", env: "stage" },
];

// 반환: "dev" | "stage" | null(신규 canonical 호스트가 아님)
// hostname 은 브라우저 location.hostname 또는 서버 Host/x-forwarded-host 를 받는다.
// 후자는 포트가 붙을 수 있어 제거한다.
const resolveCanonicalEnv = (hostname = "") => {
  const host = String(hostname || "").trim().toLowerCase().split(":")[0];
  if (!host) return null;

  for (const rule of CANONICAL_ENV_SUFFIXES) {
    if (host.endsWith(rule.suffix)) return rule.env;
  }
  return null;
};

const CANONICAL_HOSTS = {
  dev: {
    api: "https://api.dev.livee.kr",
    ws: "wss://ws.dev.livee.kr",
    hub: "https://hc.dev.livee.kr",
  },
  stage: {
    api: "https://api.stage.livee.kr",
    ws: "wss://ws.stage.livee.kr",
    hub: "https://hc.stage.livee.kr",
  },
};

// 사이트 도메인 접미사(2026-07-30). 사이트 생성 시 입력창에는 라벨만 받고
// 환경에 맞는 접미사를 붙인다. prod 는 접미사가 짧아 라벨이 곧 서브도메인이다.
const SITE_DOMAIN_SUFFIX = {
  prod: ".livee.kr",
  stage: ".stage.livee.kr",
  dev: ".dev.livee.kr",
};

// 사이트 라벨로 쓸 수 없는 이름.
//
// Route 53 실측(2026-07-30)으로 뽑았다. 플랫폼 서비스 호스트와 겹치면 그 호스트를
// 가로챈다 — 예컨대 prod 에서 라벨 'api' 는 api.livee.kr 이 되어 API 를 덮는다.
// 'dev'/'stage' 는 환경 루트(dev.livee.kr)를 덮는다.
//
//   prod  livee.kr        : api hc fms cdn ccdn ws ws-ecs www analytics log
//   dev   dev.livee.kr    : api hc fms cdn ccdn ws
//   stage stage.livee.kr  : api hc fms cdn ccdn ws
//
// 환경별로 나누지 않고 합집합으로 막는다. 사이트를 환경 간 이동할 수 있어야 하고,
// 한 환경에서만 예약이면 다른 환경으로 옮길 때 충돌한다.
const RESERVED_SITE_LABELS = [
  // 환경 접두어
  "dev", "stage", "prod", "local",
  // 플랫폼 서비스
  "api", "hc", "hub", "fms", "cdn", "ccdn", "ws", "ws-ecs",
  "console", "www", "analytics", "log",
  // 과거/테스트 호스트 (site.js 기존 차단 목록 유지)
  "api-test", "ws-test", "test1",
];

// 사이트 라벨 검증. 반환: null(정상) | 사용자에게 보일 메시지
//
// 점을 막는 이유: prod 에서 'jaekwon.dev' 를 입력하면 jaekwon.dev.livee.kr 이 되어
// dev 와일드카드 영역과 충돌한다. 라벨 하나만 받으면 이 경로가 막힌다.
const validateSiteLabel = (label = "") => {
  const value = String(label || "").trim().toLowerCase();

  if (!value) return "도메인을 입력해 주세요.";
  if (value.includes(".")) return "도메인 이름만 입력해 주세요. 점(.)은 사용할 수 없습니다.";
  if (!/^[a-z0-9-]+$/.test(value)) return "영문 소문자, 숫자, 하이픈(-)만 사용할 수 있습니다.";
  if (value.startsWith("-") || value.endsWith("-")) return "하이픈(-)으로 시작하거나 끝날 수 없습니다.";
  if (value.length > 63) return "도메인 이름은 63자 이하여야 합니다.";
  if (RESERVED_SITE_LABELS.includes(value)) return `'${value}' 는 사용할 수 없는 이름입니다.`;

  return null;
};

module.exports = {
  CANONICAL_ENV_SUFFIXES,
  CANONICAL_HOSTS,
  RESERVED_SITE_LABELS,
  SITE_DOMAIN_SUFFIX,
  resolveCanonicalEnv,
  validateSiteLabel,
};
